import type { Header, SiteInfo } from "../replay/format";
import type { WorldState } from "./world";
import { recipes } from "./recipes";

/*
 * What each mine and plant is doing: its output over the last minute, and
 * when it's made nothing, why (an input missing at its station, no people,
 * no ore left, damaged, its depot full). Worked out from frames (sites'
 * counters, stations' freight) and the recipes, which mirror
 * crates/ferro-sim/src/goods.rs.
 */

/** A recipe: goods in and out per line per run (`goods::RECIPES`). */
interface Recipe {
  inputs: [string, number][];
  outputs: [string, number][];
}

const RECIPES: Record<string, Recipe> = {
  Steelworks: { inputs: [["Iron ore", 2], ["Coal", 1]], outputs: [["Steel", 1]] },
  "Copper smelter": { inputs: [["Copper ore", 3], ["Coal", 1]], outputs: [["Copper", 1]] },
  Refinery: { inputs: [["Oil", 2]], outputs: [["Fuel", 1]] },
  "Recruiting office": { inputs: [], outputs: [["Recruits", 1]] },
  "Munitions plant": { inputs: [["Steel", 1]], outputs: [["Munitions", 20]] },
  /** Trains into its station's fleet (not a good its station holds). */
  "Locomotive works": { inputs: [["Steel", 100]], outputs: [["Trains", 1]] },
  /** Strikes copper into credits (straight to the ledger, not a good). */
  Mint: { inputs: [["Copper", 1]], outputs: [["Credits", 1]] },
};

/** Lines a plant runs at once when the header doesn't say (recordings
 *  before v29.46; a built plant's `industry::Kind::Plant::lines`): a run
 *  takes and makes this many times its recipe. */
const LINES = 8;

/** Seconds of history a rate is measured over. */
const WINDOW_S = 60;

export interface SiteStatus {
  /** Made per minute (units of its output); null until measured. */
  rate: number | null;
  /** What it makes ("steel", "iron ore"). */
  makes: string;
  /** Why it's made nothing lately, in a word or two ("No coal"); absent
   *  while it works (or before it's been watched long enough). */
  why?: string;
  /** The reason in full, with what it needs. */
  detail?: string;
  /** Resting, not at fault (a recruiting office with recruits enough):
   *  said calmly on its card, never as a warning. */
  note?: string;
  /** Not linked by rail to what it needs (the server's no_track or
   *  cut_off), the good it's short of, and the whole sentence. */
  link?: "no_track" | "cut_off";
  good?: string;
  long?: string;
}

/** A commander's site as their state reports it (`industry`). */
interface ServerSite {
  at: [number, number];
  name: string;
  working: boolean;
  made_per_min: number;
  why?: "lost" | "building" | "depleted" | "output_full" | "inputs" | "no_people" | "enough_recruits" | "no_track" | "cut_off" | "unused";
  missing?: string[];
}

/** A commander's barracks as their state reports it (`barracks`). */
interface ServerBarracks {
  at: [number, number];
  waiting: boolean;
  why?: "steel" | "recruits" | "goods" | "titan_at_foundry" | "foundry_titans_only" | "upkeep_unpaid" | "no_camp" | "at_cap";
  /** Its limits (v31.3; 0: none): steel to keep at its station after a
   *  unit's bill, and how many of its design the owner may have alive. */
  keep_steel?: number;
  max_units?: number;
  /** Its policy (v31.7), when one is attached. */
  policy?: { name: string; yaml: string; cost: number; last_rule: number | null };
  /** What its station is out of, by name (why "goods"). */
  missing?: string[];
  /** A foundry: makes titans, and only titans. */
  foundry?: boolean;
  /** Units made a minute, and of what design, when the server says. */
  made_per_min?: number;
  design?: number;
  design_name?: string;
}

/** The server's reasons, in the words the map and the tab use. */
const SERVER_WHY: Record<string, [string, string]> = {
  lost: ["Destroyed", "It's been destroyed: engineers rebuild it with steel from its station."],
  building: ["Damaged", "Below working order, it doesn't run: engineers repair it with steel from its station."],
  depleted: ["No ore left", "The ore in its reach is all dug out: build a mine somewhere new."],
  output_full: ["Station full", "Its station has no room for what it makes: trains must take it away."],
  no_people: ["No people", "Everyone who can serve has: your population grows back slowly."],
};

/** What takes a good, in words ("a steelworks or a copper smelter"), from
 *  the recipes that list it as an input. */
export function takersOf(good: string): string {
  // The server's recipes once a state lists them, else the mirror here.
  const listed = recipes().filter((r) => r.inputs.some(([, g]) => g === good)).map((r) => r.name);
  const names = (listed.length ? listed : Object.entries(RECIPES).filter(([, r]) => r.inputs.some(([g]) => g === good)).map(([n]) => n)).map((n) => n.toLowerCase());
  const a = (n: string) => (/^[aeiou]/.test(n) ? "an " : "a ") + n;
  return names.length ? names.slice(0, 2).map(a).join(" or ") : "a plant that uses it";
}

/** What a mine digs ("Iron ore mine" digs "Iron ore"). */
const mined = (site: SiteInfo) => site.name.replace(/ mine$/, "");

export class IndustryWatch {
  private history: { t: number; made: number }[][] = [];
  private header: Header | null = null;
  private lastFrame = -1;
  /** A commander's people to recruit from (their state), else unknown. */
  pool: number | null = null;
  /** A commander's sites and barracks as their state reports them, by
   *  hex ("q,r"): their word wins over what frames suggest. */
  private server = new Map<string, ServerSite[]>();
  private serverBarracks = new Map<string, ServerBarracks>();
  /** The commander's stations' freight room (one capacity, every good). */
  private capacity = new Map<number, number>();

  /** The commander's state: their industry's and barracks' reasons. */
  setState(state: Record<string, unknown>): void {
    this.pool = (state.population as { pool?: number } | undefined)?.pool ?? null;
    this.server.clear();
    for (const x of (state.industry as ServerSite[] | undefined) ?? []) {
      const k = `${x.at[0]},${x.at[1]}`;
      this.server.set(k, [...(this.server.get(k) ?? []), x]);
    }
    this.serverBarracks = new Map(((state.barracks as ServerBarracks[] | undefined) ?? []).map((b) => [`${b.at[0]},${b.at[1]}`, b]));
    const stations = (state.stations as { id: number; freight_capacity?: number }[] | undefined) ?? [];
    this.capacity = new Map(stations.filter((s) => s.freight_capacity !== undefined).map((s) => [s.id, s.freight_capacity!]));
  }

  /** What the barracks at a hex made in the last minute, if the state
   *  says: units, and their design (a name, or an id). */
  barracksRate(q: number, r: number): { n: number; design?: string | number } | null {
    const b = this.serverBarracks.get(`${q},${r}`);
    return b?.made_per_min === undefined ? null : { n: b.made_per_min, design: b.design_name ?? b.design };
  }

  /** Why the barracks at a hex isn't training, if the state says: the
   *  word, and in full. */
  barracksWhy(q: number, r: number): [string, string] | null {
    const b = this.serverBarracks.get(`${q},${r}`);
    if (!b?.waiting || !b.why) return null;
    if (b.why === "at_cap") return ["At its cap", `At its cap: ${b.max_units ?? "the most"} of this design alive. It makes more as they fall, or raise the limit on its card.`];
    if (b.why === "upkeep_unpaid") return ["Upkeep unpaid", "Your army's steel upkeep went unpaid: no barracks makes anything until there's steel for it."];
    if (b.why === "no_camp") return ["Needs a training camp", "This design has training: it's made only at a barracks with a training camp of yours within 12 hexes. Build one near."];
    if (b.why === "titan_at_foundry") return ["Titans need a foundry", "A barracks can't make a titan: set this one to another design, and build a foundry for titans."];
    if (b.why === "foundry_titans_only") return ["Titans only", "A foundry makes only titans: set it to a titan design."];
    if (b.why === "goods" || (b.missing?.length && b.why !== "recruits")) {
      const names = (b.missing ?? []).map((g) => g.toLowerCase());
      const list = names.length ? names.join(", ") : "what it needs";
      return [`No ${list}`, `Its station is out of ${names.length ? names.join(" and ") : "what the design needs"}: it comes by rail from the plants that make it.`];
    }
    return b.why === "steel"
      ? ["No steel", "Its station has no steel to pay for the next unit: steel comes by rail from a steelworks."]
      : ["No recruits", "Its station has no recruits to train: they come by rail from home, which signs up your people."];
  }

  /** The policy on the barracks at a hex (v31.7), if any. */
  barracksPolicy(q: number, r: number): { name: string; yaml: string; cost: number; last_rule: number | null } | null {
    return this.serverBarracks.get(`${q},${r}`)?.policy ?? null;
  }

  /** A barracks' limits at a hex (v31.3), or null where the server keeps
   *  none (an older server). */
  barracksLimits(q: number, r: number): { keep: number; max: number } | null {
    const b = this.serverBarracks.get(`${q},${r}`);
    return b && typeof b.keep_steel === "number" ? { keep: b.keep_steel, max: b.max_units ?? 0 } : null;
  }

  /** Whether the producer at a hex is a foundry (titans only). */
  isFoundry(q: number, r: number): boolean {
    return !!this.serverBarracks.get(`${q},${r}`)?.foundry;
  }

  /** Follows a new world (its header lists the sites). */
  reset(header: Header): void {
    this.header = header;
    this.history = (header.sites ?? []).map(() => []);
    this.lastFrame = -1;
  }

  /** Samples each site's counter, once a frame. */
  update(state: WorldState): void {
    if (!this.header || state.frame === this.lastFrame) return;
    if (state.frame < this.lastFrame) this.history.forEach((h) => (h.length = 0));
    this.lastFrame = state.frame;
    const t = state.seconds();
    state.sites.forEach((st, i) => {
      const h = this.history[i];
      if (!h) return;
      h.push({ t, made: st.output });
      while (h.length > 2 && t - h[1].t >= WINDOW_S) h.shift();
    });
  }

  /** Why the structure at a station isn't working, if none of its sites
   *  is (a mine digging two ores works while either does): the first
   *  site's reason, else null. */
  stationIdle(station: number, state: WorldState): SiteStatus | null {
    const sites = this.sitesAt(station);
    const all = sites.map((i) => this.status(i, state));
    return all.length && all.every((x) => x?.why) ? all[0] : null;
  }

  /** Each station's sites (indexes), built once per header: asked for every
   *  structure drawn, every frame, on maps with thousands of sites. */
  private byStation: { sites: unknown; map: Map<number, number[]> } | null = null;
  private sitesAt(station: number): number[] {
    const list = this.header?.sites ?? [];
    if (this.byStation?.sites !== list) {
      const map = new Map<number, number[]>();
      list.forEach((x, i) => {
        const at = map.get(x.station);
        if (at) at.push(i);
        else map.set(x.station, [i]);
      });
      this.byStation = { sites: list, map };
    }
    return this.byStation.map.get(station) ?? [];
  }

  /** Site `i`'s status now. */
  status(i: number, state: WorldState): SiteStatus | null {
    const site = this.header?.sites?.[i];
    const st = state.sites[i];
    if (!site || !st) return null;
    const recipe = site.kind === "plant" ? RECIPES[site.name] : undefined;
    const made = recipe?.outputs[0];
    const makes = (site.kind === "mine" ? mined(site) : (made?.[0] ?? "goods")).toLowerCase();
    const h = this.history[i] ?? [];
    const span = h.length ? h[h.length - 1].t - h[0].t : 0;
    // Output per counter step: a mine counts units, a plant runs.
    const lines = site.lines || LINES;
    const per = site.kind === "mine" ? 1 : (made?.[1] ?? 1) * lines;
    const rate = span >= 10 ? (((h[h.length - 1].made - h[0].made) * 60) / span) * per : null;
    const out: SiteStatus = { rate, makes };
    // Its station closed: being taken down (or gone), not broken.
    if (state.depots[site.station]?.faction === 255) {
      return { ...out, rate: null, why: "Decommissioning", detail: "Being taken down: three quarters of its steel, and what it held, go back to your home." };
    }
    // The commander's own: the server knows why.
    const w = state.width;
    const told = this.server.get(`${site.tile % w},${Math.floor(site.tile / w)}`)?.find((x) => x.name === site.name);
    if (told) {
      out.rate = told.made_per_min;
      if (told.working || !told.why) return out;
      if (told.why === "enough_recruits") {
        return { ...out, note: "Recruits enough", detail: "Its station holds plenty of recruits: it signs up more as they're taken." };
      }
      // Not linked by rail (Factorio's "not connected"): the fix is track,
      // not more of the good.
      // A mine's ore going nowhere (v31.15): off the rail, or on a
      // network where nothing takes it.
      if (site.kind === "mine" && told.why === "no_track") {
        const ore = mined(site);
        return { ...out, why: "Not on rail", link: "no_track", good: ore, long: "Not on rail: its ore can't leave", detail: `Its station isn't on track, so its ${ore.toLowerCase()} piles up there: lay track from it to a plant that takes ${ore.toLowerCase()}.` };
      }
      if (site.kind === "mine" && told.why === "unused") {
        const ore = mined(site);
        const takers = takersOf(ore);
        return {
          ...out,
          why: "Ore unused",
          good: ore,
          long: `Nothing on its rail takes ${ore.toLowerCase()}`,
          detail: `Nothing on its rail network takes ${ore.toLowerCase()}, so it piles up at its station: build ${takers} on that track.`,
        };
      }
      if (told.why === "no_track") {
        const good = told.missing?.[0];
        return { ...out, why: "Not on rail", link: "no_track", good, long: "Not on rail", detail: "Its station isn't on track, so no train can bring what it needs: join it to your rail with track." };
      }
      if (told.why === "cut_off") {
        const missing = told.missing ?? [];
        const good = missing[0];
        const list = missing.map((g) => g.toLowerCase()).join(" or ") || "what it needs";
        return {
          ...out,
          why: "Cut off",
          link: "cut_off",
          good,
          long: `Cut off: nothing on its rail network has or makes ${good ?? "what it needs"}`,
          detail: `Nothing on its rail network holds spare ${list} or makes it: join its track to a network that does, or build what makes it on this one.`,
        };
      }
      if (told.why === "inputs") {
        const missing = (told.missing ?? []).map((g) => g.toLowerCase());
        return { ...out, why: `No ${missing[0] ?? "inputs"}`, detail: `Its station is out of ${missing.join(" and ") || "what it needs"}.` };
      }
      const [word, detail] = SERVER_WHY[told.why] ?? ["Idle", ""];
      return { ...out, why: word, detail };
    }
    // Working, or not watched long enough to say.
    if (rate === null || rate > 0) return out;
    const why = (short: string, detail: string) => ({ ...out, why: short, detail });
    if ((st.integrity * 100) / Math.max(1, site.full) < site.operational_pct) {
      return why("Damaged", `Under ${site.operational_pct}% built, it doesn't run: engineers repair it with steel from its station.`);
    }
    const names = this.header?.freight_goods ?? [];
    const freight = state.freight.get(site.station);
    const held = (good: string) => {
      const g = names.indexOf(good);
      return freight && g >= 0 ? freight[g] : null;
    };
    // Its station full of what it makes (when the room is known).
    const room = this.capacity.get(site.station);
    const full = (good: string, n: number) => room !== undefined && (held(good) ?? 0) + n > room;
    const fullWhy = (good: string) => why("Station full", `Its station holds all the ${good.toLowerCase()} it can${room === undefined ? "" : ` (${room})`}: trains must take some away, or something must use it.`);
    if (site.kind === "mine") {
      if (st.reserve === 0) return why("No ore left", "The ore in its reach is all dug out: build a mine somewhere new.");
      return fullWhy(mined(site));
    }
    if (!recipe) return why("Idle", "");
    // An input missing at its station.
    for (const [good, n] of recipe.inputs) {
      const h = held(good);
      if (h !== null && h < n * lines) {
        const needs = recipe.inputs.map(([x, k]) => `${k * lines} ${x.toLowerCase()}`).join(" and ");
        return why(`No ${good.toLowerCase()}`, `A run needs ${needs}; its station has ${h} ${good.toLowerCase()}.`);
      }
    }
    for (const [good, n] of recipe.outputs) if (good !== "Munitions" && full(good, n * lines)) return fullWhy(good);
    if (site.name === "Recruiting office" && this.pool === 0) return why("No people", "Everyone who can serve has: your population grows back slowly.");
    if (made?.[0] === "Munitions") {
      const depot = state.depots[site.station];
      if (depot && depot.capacity - depot.stock < made[1] * lines) return why("Depot full", "Its depot has no room for more rounds: trains must take them away.");
    }
    return why("Idle", "Its inputs are there: its station is likely full of what it makes, waiting for trains to take it away.");
  }
}

/** The world's industry, watched (one world at a time). */
export const industry = new IndustryWatch();
